import { gameMainDrawer } from './gameMainDrawer';
import { PixiBase } from '../../core/pixi/PixiBase';
import { containerNames, frameNames } from '../../constants';
import { flipCardEnum, gambleCardsEnum } from '../../enums';

import { gambleStore } from '../../store/gambleStore';
import { winBarStore } from '../../store/winBarStore';

import { Spine } from '../../services/elementTypeService/Spine';
import { animationService } from '../../services/animationService';

import { isMobile } from '../../helpers/isMobile';
import { handleGamble, handleCollect } from '../../helpers';
import { gameStore } from '../../store/gameStore';

export class GambleDrawer {
  constructor() {
    this.count = 1;
    this.animations = {};
    this.timeOutId = null;
    this.isCollectSend = false;
    this.gambleFinished = false;
    this.gambleAttemptsCountByRound = 1;
    this.isCardAnimationFinished = false;
    this.GambleContainer = PixiBase.createContainer(containerNames.GambleContainer);
    this.GambleContainer.visible = false;
  }

  setupGambleBackgroundContainer() {
    this.gambleBackgroundContainer = PixiBase.createContainer(
      containerNames.GambleBackgroundContainer
    );
    this.gambleBackgroundContainer.interactive = true;
    this.setupBackgroundGraphics();
    this.setupBackground();
    this.GambleContainer.addChild(this.gambleBackgroundContainer);
  }

  setupBackgroundGraphics() {
    this.backgroundGraphics = PixiBase.createGraphics();
    this.backgroundGraphics.beginFill(0x000000);
    isMobile()
      ? this.backgroundGraphics.drawRect(0, 0, 540, 960)
      : this.backgroundGraphics.drawRect(0, 0, 1440, 810);
    this.backgroundGraphics.endFill();
    this.backgroundGraphics.alpha = 0.9;
    this.gambleBackgroundContainer.addChild(this.backgroundGraphics);
  }

  setupBackground() {
    this.background = PixiBase.createSprite(this.spriteSheet.gambleBackground);
    const { x, y, anchor } = this.config.GambleContainer.backgroundConfig;
    PixiBase.setPosition(this.background, { x: x, y: y });
    this.background.anchor.set(anchor);
    this.gambleBackgroundContainer.addChild(this.background);
  }

  setupGambleHistoryContainer() {
    this.historyContainer = PixiBase.createContainer(containerNames.HistoryContainer);
    const { x, y } = this.config.GambleContainer.historyContainerConfig;
    PixiBase.setPosition(this.historyContainer, { x: x, y: y });
    this.setupHistoryTitle();
    this.setupHistoryCards();
    this.GambleContainer.addChild(this.historyContainer);
  }

  setupHistoryTitle() {
    this.historyTitle = PixiBase.createText(gameStore.canvasTranslationKeys.GAMBLE_FEATURE, {
      fontSize: isMobile() ? 12 : 16,
      fill: 'white',
      align: 'center',
      fontWeight: isMobile() ? 600 : 500,
      fontFamily: 'Roboto',
    });
    const { x, y, anchor } = this.config.GambleContainer.historyTitleConfig;
    this.historyTitle.anchor.set(anchor);
    PixiBase.setPosition(this.historyTitle, { x: x, y: y });
    this.historyContainer.addChild(this.historyTitle);
  }

  setupHistoryCards() {
    this.allCardsContainer = PixiBase.createContainer(containerNames.AllCardsContainer);
    const gambleHistoryCardsMaskGraphics = PixiBase.createGraphics();
    this.gambleHistoryCardsGraphics = PixiBase.setMaskGraphicProperties(
      gambleHistoryCardsMaskGraphics,
      isMobile() ? [-132, -5, 262, 76] : [-196, -7, 395, 123]
    );
    PixiBase.setMaskGraphicToContainer(this.historyContainer, this.gambleHistoryCardsGraphics);
    this.historyCards = PixiBase.createSprite(this.spriteSheet.historyCards);
    this.historyCards.anchor.set(0.5);
    const { x, y } = this.config.GambleContainer.historyRedCardConfig;
    PixiBase.setPosition(this.allCardsContainer, { x: x, y: y });
    this.allCardsContainer.addChild(this.historyCards);
    this.historyContainer.addChild(this.allCardsContainer);
    this.historyContainer.addChild(gambleHistoryCardsMaskGraphics);
  }

  setupHistoryCard(gambleCard) {
    const card = PixiBase.createSprite(this.spriteSheet.historyRedCard);
    card.anchor.set(0.5);
    this.card = card;
    PixiBase.changeFrame(
      card,
      gambleCard === gambleCardsEnum.black - 1
        ? frameNames.HistoryRedCard
        : frameNames.HistoryBlackCard
    );
    card.x = isMobile() ? -2 : -4;
    this.allCardsContainer.addChild(card);
    this.allCardsContainer.setChildIndex(
      this.historyCards,
      this.allCardsContainer.children.length - 1
    );
  }

  setupGambleAmountsContainer() {
    this.amountsContainer = PixiBase.createContainer(containerNames.AmountsContainer);
    const { x, y } = this.config.GambleContainer.amountsContainerConfig;
    PixiBase.setPosition(this.amountsContainer, { x: x, y: y });
    this.setupWinAmount();
    this.setupWinAmountFrame();
    this.setupWinAmountTitle();
    this.setupGambleToWinAmount();
    this.setupGambleToWinAmountTitle();
    this.GambleContainer.addChild(this.amountsContainer);
  }

  setupWinAmount() {
    this.winAmountContainer = PixiBase.createContainer(containerNames.WinAmountContainer);
    this.winAmount = PixiBase.createText('', {
      fontSize: isMobile() ? 20 : 24,
      fill: '#FFFFFF',
      align: 'center',
      fontWeight: 500,
      fontFamily: 'Roboto',
    });
    const { x, y } = this.config.GambleContainer.winAmountConfig;
    PixiBase.setPosition(this.winAmount, { x: x, y: y });
    this.winAmount.anchor.set(0.5);
    this.winAmountContainer.addChild(this.winAmount);
    this.amountsContainer.addChild(this.winAmountContainer);
  }

  setupWinAmountTitle() {
    this.winAmountTitle = PixiBase.createText(gameStore.canvasTranslationKeys.GAMBLE_AMOUNT_TEXT, {
      fontSize: isMobile() ? 14 : 20,
      fill: '#FFC33E',
      align: 'center',
      fontWeight: 500,
      fontFamily: 'Roboto',
    });
    this.winAmountTitle.anchor.set(0.5);
    this.winAmountContainer.addChild(this.winAmountTitle);
  }

  setupWinAmountFrame() {
    for (let i = 0; i < 2; i += 1) {
      const winAmountFrame = PixiBase.createSprite(this.spriteSheet.winAmountFrame);
      const { y, anchor } = this.config.GambleContainer.winAmountFrameConfig;
      PixiBase.setPosition(winAmountFrame, {
        x: i * (2 * winAmountFrame.width + (isMobile() ? -7 : 71)),
        y: y,
      });
      winAmountFrame.anchor.set(anchor);
      this.winAmountContainer.addChild(winAmountFrame);
    }
  }

  setupGambleToWinAmount() {
    this.gambleToWinAmountContainer = PixiBase.createContainer(
      containerNames.GambleToWinAmountContainer
    );
    const { x } = this.config.GambleContainer.gambleToWinAmountContainer;
    PixiBase.setPosition(this.gambleToWinAmountContainer, { x: x });
    this.gambleToWinAmount = PixiBase.createText('', {
      fontSize: isMobile() ? 20 : 24,
      fill: '#FFFFFF',
      align: 'center',
      fontWeight: 600,
      fontFamily: 'Roboto',
    });
    const { x: amountX, y: amountY } = this.config.GambleContainer.gambleToWinAmountConfig;
    PixiBase.setPosition(this.gambleToWinAmount, { x: amountX, y: amountY });
    this.gambleToWinAmount.anchor.set(0.5);
    this.gambleToWinAmountContainer.addChild(this.gambleToWinAmount);
    this.amountsContainer.addChild(this.gambleToWinAmountContainer);
  }

  setupGambleToWinAmountTitle() {
    this.gambleToWinAmountTitle = PixiBase.createText(
      gameStore.canvasTranslationKeys.GAMBLE_TO_WIN_TEXT,
      {
        fontSize: isMobile() ? 14 : 20,
        fill: '#FFC33E',
        align: 'center',
        fontWeight: 500,
        fontFamily: 'Roboto',
      }
    );
    this.gambleToWinAmountTitle.anchor.set(0.5);
    this.gambleToWinAmountContainer.addChild(this.gambleToWinAmountTitle);
  }

  setupGambleMainContainer() {
    this.gambleMainContainer = PixiBase.createContainer(containerNames.GambleMainContainer);
    const { x, y } = this.config.GambleContainer.gambleMainContainerConfig;
    PixiBase.setPosition(this.gambleMainContainer, { x: x, y: y });
    this.setupRedButton();
    this.setupBlackButton();
    this.setupFlipCard();
    this.setupYouWin();
    this.setupBlackSymbol();
    this.setupRedSymbol();
    this.GambleContainer.addChild(this.gambleMainContainer);
  }

  setupYouWin() {
    this.youWin = PixiBase.createSprite(this.spriteSheet.youWin);
    this.youWin.visible = false;
    const { x, y, anchor } = this.config.GambleContainer.youWinConfig;
    PixiBase.setPosition(this.youWin, { x: x, y: y });
    this.youWin.anchor.set(anchor);
    this.gambleMainContainer.addChild(this.youWin);
  }

  updateWinAndGambleToWinAmount() {
    this.winAmount.text = winBarStore.winAmount;
    this.gambleToWinAmount.text = 2 * winBarStore.winAmount;
  }

  setupRedButton() {
    this.redButtonContainer = PixiBase.createContainer(containerNames.RedButtonContainer);
    const { y } = this.config.GambleContainer.redButtonContainerConfig;
    PixiBase.setPosition(this.redButtonContainer, { y: y });
    this.redButtonContainer.id = gambleCardsEnum.red;
    this.redButtonStandard = PixiBase.createSprite(this.spriteSheet.redButtonStandard);
    this.redButtonStandard.anchor.set(0.5);
    PixiBase.setupButtonMode(this.redButtonContainer, { interactive: true, buttonMode: true });
    PixiBase.registerClick(this.redButtonContainer, () => {
      if (this.isCollectSend || this.gambleFinished) return;
      PixiBase.changeFrame(this.redButtonStandard, frameNames.RedButtonPressed);
    });
    PixiBase.registerPointerover(this.redButtonContainer, () => {
      if (!isMobile()) PixiBase.changeFrame(this.redButtonStandard, frameNames.RedButtonHovered);
    });
    PixiBase.registerPointerup(this.redButtonContainer, (event) => {
      if (this.isCollectSend || this.gambleFinished) return;
      if (!this.isCardAnimationFinished) {
        this.isCardAnimationFinished = true;
        handleGamble(event);
        PixiBase.changeFrame(this.redButtonStandard, frameNames.RedButtonStandard);
      }
    });
    PixiBase.registerPointerout(this.redButtonContainer, () => {
      PixiBase.changeFrame(this.redButtonStandard, frameNames.RedButtonStandard);
    });
    this.redButtonContainer.addChild(this.redButtonStandard);
    this.gambleMainContainer.addChild(this.redButtonContainer);
  }

  setupRedSymbol() {
    this.redSymbol = new Spine({ key: 'symbol' });
    this.redSymbol.skeleton.setSkinByName('heart');
    this.redSymbol.play('idle', true);
    this.redButtonContainer.addChild(this.redSymbol);
  }

  setupBlackButton() {
    this.blackButtonContainer = PixiBase.createContainer(containerNames.BlackButtonContainer);
    const { x, y } = this.config.GambleContainer.blackButtonContainerConfig;
    PixiBase.setPosition(this.blackButtonContainer, { x: x, y: y });
    this.blackButtonContainer.id = gambleCardsEnum.black;
    this.blackButtonStandard = PixiBase.createSprite(this.spriteSheet.blackButtonStandard);
    this.blackButtonStandard.anchor.set(0.5);
    PixiBase.setupButtonMode(this.blackButtonContainer, { interactive: true, buttonMode: true });
    PixiBase.registerClick(this.blackButtonContainer, () => {
      if (this.isCollectSend || this.gambleFinished) return;
      PixiBase.changeFrame(this.blackButtonStandard, frameNames.BlackButtonPressed);
    });
    PixiBase.registerPointerover(this.blackButtonContainer, () => {
      if (!isMobile())
        PixiBase.changeFrame(this.blackButtonStandard, frameNames.BlackButtonHovered);
    });
    PixiBase.registerPointerup(this.blackButtonContainer, (event) => {
      if (this.isCollectSend || this.gambleFinished) return;
      if (!this.isCardAnimationFinished) {
        this.isCardAnimationFinished = true;
        handleGamble(event);
        PixiBase.changeFrame(this.blackButtonStandard, frameNames.BlackButtonStandard);
      }
    });
    PixiBase.registerPointerout(this.blackButtonContainer, () => {
      PixiBase.changeFrame(this.blackButtonStandard, frameNames.BlackButtonStandard);
    });
    this.blackButtonContainer.addChild(this.blackButtonStandard);
    this.gambleMainContainer.addChild(this.blackButtonContainer);
  }

  flipCardAnimation(resolve) {
    this.flipCard.clearListeners();
    this.flipCard.play('flip', false);
    this.flipCard.addListener(
      'complete',
      () => {
        if (this.isGambleWin) {
          gameMainDrawer.gambleBar.youWin.visible = true;
          clearTimeout(this.timeOutId);
          this.timeOutId = setTimeout(() => {
            this.flipCard.clearListeners();
            this.flipCard.play('idle', true);
            resolve();
            this.flipCard.addListener(
              'complete',
              () => {
                this.flipCard.clearListeners();
              },
              this,
              'idle'
            );
            gameMainDrawer.gambleBar.youWin.visible = false;
          }, 500);
        }
      },
      this,
      'flip'
    );
  }

  historyContainerAnimation() {
    this.count < 6 &&
      animationService.handleHistoryContainerAnimation({
        container: this.allCardsContainer,
      });
  }

  handleCardsAnimation(gambleWinCard) {
    return new Promise((resolve) => {
      this.historyContainerAnimation();
      this.flipCardAnimation(resolve);
      this.updateFlipCard(gambleWinCard);
      this.allCardsContainer.children.forEach((card, index) => {
        if (index !== this.allCardsContainer.children.length - 1) {
          card.id = index + 1;
        }
        if (this.isCardAnimationFinished) {
          animationService.handleHistoryCardAnimation({
            card: card,
            fn: resolve,
            count: this.count,
            isGambleWin: this.isGambleWin,
            gambleFinished: this.gambleFinished,
            isLast: index === this.allCardsContainer.children.length - 1,
          });
        }
      });
      this.gambleAttemptsCountByRound += 1;
      this.count += 1;
    });
  }
  hideGamble() {
    this.GambleContainer.visible = false;
  }

  handleGambleFinished(totalWinAmount, isGambleWin) {
    gameMainDrawer.spinBar.pgLogo.visible = true;
    gameMainDrawer.spinBar.spinButton.visible = true;
    gameMainDrawer.spinBar.spinButtonArrows.visible = true;
    gambleStore.setGambleCount(0);
    isGambleWin
      ? gambleStore.setGambleTotalWinAmount(totalWinAmount)
      : gambleStore.setGambleTotalWinAmount(0);
    gameMainDrawer.betBar.updateLastWinAmount(totalWinAmount || 0);
    setTimeout(() => {
      this.gambleAttemptsCountByRound = 1;
      gambleStore.setIsGamble(false);
      this.flipCard.stop();
      this.youWin.visible = false;
      gambleStore.setIsGamblePopupVisible(false);
      totalWinAmount && winBarStore.setPrevWinAmount(totalWinAmount);
      this.GambleContainer.visible = false;
    }, 500);
  }

  updateWinAndToWinAmounts() {
    this.winAmount.text = this.gambleToWinAmount.text;
    this.gambleToWinAmount.text = 2 * +this.winAmount.text;
  }

  updateAttemptsLeftCount(attemptsLeftCount) {
    this.attemptsLeftCount.text = attemptsLeftCount;
  }

  updateFlipCard(gambleWinCard) {
    this.flipCard.skeleton.setSkinByName(
      gambleWinCard === gambleCardsEnum.black - 1 ? flipCardEnum.red : flipCardEnum.black
    );
  }

  handleGambleResultData({
    isGambleWin,
    gambleWinCard,
    gambleAttemptsCount,
    totalWinAmount,
    gambleFinished,
  }) {
    this.youWin.visible = false;
    this.isGambleWin = isGambleWin;
    this.gambleWinCard = gambleWinCard;
    this.gambleFinished = gambleFinished;
    this.totalWinAmount = totalWinAmount;
    this.gambleAttemptsCount = gambleAttemptsCount;
    if (gambleStore.gambleCount === this.gambleAttemptsCountByRound) {
      this.attemptsLeftCount.visible = false;
      this.updateAttemptLeftText(gameStore.canvasTranslationKeys.GAMBLE_ATTEMPTS_FINISHED_TEXT);
    }
    this.handleCardsAnimation(gambleWinCard).then(() => {
      gambleFinished
        ? this.handleGambleFinished(totalWinAmount, isGambleWin)
        : this.updateWinAndToWinAmounts();
      gameMainDrawer.gambleBar.isCardAnimationFinished = false;
    });
  }

  setupBlackSymbol() {
    this.blackSymbol = new Spine({ key: 'symbol' });
    this.blackSymbol.skeleton.setSkinByName('spider');
    this.blackSymbol.play('idle', true);
    this.blackButtonContainer.addChild(this.blackSymbol);
  }

  setupFlipCard() {
    this.flipCard = new Spine({ key: 'card' });
    this.flipCard.play('idle', true);
    const { x } = this.config.GambleContainer.flipCardConfig;
    PixiBase.setPosition(this.flipCard, { x: x });
    this.gambleMainContainer.addChild(this.flipCard);
  }

  setupAttemptsLftContainer() {
    this.attemptsLftContainer = PixiBase.createContainer(containerNames.AttemptsLftContainer);
    const { y } = this.config.GambleContainer.attemptsLftContainerConfig;
    PixiBase.setPosition(this.attemptsLftContainer, { y: y });
    this.attemptsLftContainer.x = this.GambleContainer.width / 2;
    this.setupAttemptsLeftText();
    this.setupAttemptsLeftCount();
    this.GambleContainer.addChild(this.attemptsLftContainer);
  }

  setupAttemptsLeftText() {
    this.attemptsLeftText = PixiBase.createText(
      gameStore.canvasTranslationKeys.GAMBLE_ATTEMPTS_LEFT_TEXT,
      {
        fontSize: isMobile() ? 12 : 20,
        fill: 'white',
        align: 'center',
        fontWeight: isMobile() ? 600 : 500,
        fontFamily: 'Roboto',
      }
    );
    this.attemptsLeftText.anchor.set(0.5);
    this.attemptsLftContainer.addChild(this.attemptsLeftText);
  }

  updateAttemptLeftText(text) {
    this.attemptsLeftText.text = text;
  }

  setupAttemptsLeftCount() {
    this.attemptsLeftCount = PixiBase.createText('', {
      fontSize: isMobile() ? 15 : 18,
      fill: '#FFC33E',
      align: 'center',
      fontWeight: 600,
      fontFamily: 'Roboto',
    });
    this.attemptsLeftCount.x = this.attemptsLeftText.width / 2 + 13;
    this.attemptsLeftCount.y = 1;
    this.attemptsLeftCount.anchor.set(0.5);
    this.attemptsLftContainer.addChild(this.attemptsLeftCount);
  }

  setupCollectButton() {
    this.collectButtonStandard = PixiBase.createSprite(this.spriteSheet.collectButtonStandard);
    const { x, y } = this.config.GambleContainer.collectContainerConfig;
    this.collectButtonGraphics = PixiBase.createGraphics();
    this.collectButtonGraphics.beginFill(0x00000);
    this.collectButtonGraphics.drawCircle(0, 0, 70);
    this.collectButtonGraphics.endFill();
    this.collectButtonStandard.anchor.set(0.5);
    this.collectButtonGraphics.alpha = 0;
    PixiBase.setPosition(this.collectButtonStandard, { x: x, y: y });
    PixiBase.setPosition(this.collectButtonGraphics, { x: x, y: y });
    PixiBase.setupButtonMode(this.collectButtonGraphics, { interactive: true, buttonMode: true });
    PixiBase.registerClick(this.collectButtonGraphics, () => {
      if (this.isCollectSend || this.gambleFinished) return;
      PixiBase.changeFrame(this.collectButtonStandard, frameNames.CollectButtonPressed);
    });
    PixiBase.registerPointerup(this.collectButtonGraphics, () => {
      if (this.isCollectSend || this.gambleFinished) return;
      this.isCollectSend = true;
      gambleStore.setIsCollectBeforeSpin(null);
      handleCollect().then(() => {
        this.isCollectSend = false;
        this.youWin.visible = false;
        this.GambleContainer.visible = false;
        PixiBase.changeFrame(this.collectButtonStandard, frameNames.CollectButtonStandard);
      });
    });
    PixiBase.registerPointerout(this.collectButtonGraphics, () => {
      PixiBase.changeFrame(this.collectButtonStandard, frameNames.CollectButtonStandard);
    });
    this.GambleContainer.addChild(this.collectButtonStandard);
    this.GambleContainer.addChild(this.collectButtonGraphics);
  }

  execute(config, spriteSheet) {
    const [gameImageSpriteSheet] = spriteSheet;
    this.config = config;
    this.spriteSheet = gameImageSpriteSheet;
    this.setupGambleBackgroundContainer();
    this.setupGambleHistoryContainer();
    this.setupGambleAmountsContainer();
    this.setupGambleMainContainer();
    this.setupAttemptsLftContainer();
    this.setupCollectButton();
  }
}
